import { Component, OnInit, ViewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import {
  Validators,
  FormGroup,
  FormBuilder,
  ValidationErrors,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms';
import { UserService } from '../../../services/user.service';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../../services/student-authentication.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorService } from 'app/shared/services/error.service';
import { AppLoaderService } from 'app/shared/services/app-loader/app-loader.service';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
})
export class ResetPasswordComponent implements OnInit {
  @ViewChild(MatButton) submitButton: MatButton;

  resetPasswordForm: FormGroup;
  showResetInvalid = false;
  resetErrorMessage: string;
  user = {
    email: '',
  };

  error_messages = {
    email: [
      { type: 'required', message: 'Please enter your email' },
      { type: 'email', message: 'Please enter a valid email.' },
    ],
    code: [
      { type: 'required', message: 'Please enter your confirmation code.' },
    ],
    password: [
      { type: 'required', message: 'Please enter a new password.' },
      {
        type: 'minlength',
        message: 'Password must be at least 6 characters long.',
      },
      {
        type: 'hasUppercase',
        message: 'Password must contain at least 1 uppercase letter.',
      },
      {
        type: 'hasLowercase',
        message: 'Password must contain at least 1 lowercase letter.',
      },
      {
        type: 'hasNumber',
        message: 'Password must contain at least 1 number.',
      },
      {
        type: 'hasSpecialCharacter',
        message: 'Password must contain at least 1 special character.',
      },
    ],
    confirmPassword: [
      { type: 'required', message: 'Please confirm your new password.' },
      { type: 'mustMatch', message: 'Passwords must match.' },
    ],
  };

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private router: Router,
    private authenticationService: AuthenticationService,
    private _snackBar: MatSnackBar,
    private errorService: ErrorService,
    private loader: AppLoaderService
  ) {}

  ngOnInit() {
    if (this.userService.user?.username) {
      this.user.email = this.userService.user.username;
    }

    this.resetPasswordForm = this.formBuilder.group(
      {
        email: [this.user.email, [Validators.required, Validators.email]],
        code: ['', Validators.required],
        password: [
          '',
          Validators.compose([
            // Contains any input
            Validators.required,
            // Contains at least 1 number
            this.patternValidator(/\d/, { hasNumber: true }),
            // Contains at least 1 uppercase letter
            this.patternValidator(/[A-Z]/, { hasUppercase: true }),
            // Contains at least 1 lowercase letter
            this.patternValidator(/[a-z]/, { hasLowercase: true }),
            // Contains at least 1 special character
            this.patternValidator(/[^A-Za-z0-9]/, {
              hasSpecialCharacter: true,
            }),
            // Contains at least 6 characters
            Validators.minLength(6),
          ]),
        ],
        confirmPassword: ['', Validators.required],
      },
      {
        validators: this.fieldMatch('password', 'confirmPassword'),
      }
    );
  }

  get formControls() {
    return this.resetPasswordForm.controls;
  }

  async onSubmit() {
    this.submitButton.disabled = true;
    if (!this.resetPasswordForm.invalid) {
      this.showResetInvalid = false;
      const resetPasswordData = this.resetPasswordForm.value;
      try {
        this.loader.open('Please wait');
        await this.authenticationService.resetPassword(resetPasswordData);
        this.loader.close();
        this._snackBar.open('Password reset successful', 'Close', {
          duration: 8000,
          panelClass: ['success-snackbar'],
        });

        this.router.navigate(['/sessions/signin']);
      } catch (err) {
        this.loader.close();
        this.showResetInvalid = true;
        this.resetErrorMessage = this.errorService.mapError(
          JSON.parse(err.response).message
        );
        this.submitButton.disabled = false;
      }
    }
  }

  fieldMatch(controlName: string, matchingControlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        // return if another validator has already found an error on the matchingControl
        return;
      }

      // set error on matchingControl if validation fails
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        // if control is empty return no error
        return null;
      }

      // test the value of the control against the regexp supplied
      const valid = regex.test(control.value);

      // if true, return no error (no error), else return error passed in the second parameter
      return valid ? null : error;
    };
  }
}
